import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { pl } from './pl';
import { en } from './en';

export const LOCALES = ['pl', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'overseer.locale';

const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/**
 * The locale to start in: a saved choice, then the browser's preference, then
 * Polish.
 *
 * Polish is the fallback rather than English because every current user is
 * Polish-speaking. English exists so a second locale is possible at all —
 * retrofitting i18n later would be the expensive path.
 */
const initialLocale = (): Locale => {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(saved)) {
      return saved;
    }
  } catch {
    // Private browsing. Fall through to the browser preference.
  }

  const preferred = navigator.languages?.find((language) => isLocale(language.slice(0, 2)));

  return isLocale(preferred?.slice(0, 2)) ? (preferred.slice(0, 2) as Locale) : 'pl';
};

export const initI18n = (): typeof i18next => {
  void i18next.use(initReactI18next).init({
    resources: {
      pl: { translation: pl },
      en: { translation: en },
    },
    lng: initialLocale(),
    fallbackLng: 'pl',
    interpolation: {
      // React escapes for us; double-escaping turns an apostrophe into `&#39;`
      // on screen.
      escapeValue: false,
    },
    returnNull: false,
  });

  document.documentElement.lang = i18next.language;

  return i18next;
};

export const setLocale = async (locale: Locale): Promise<void> => {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;

  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // The choice applies to this session even if it cannot be remembered.
  }
};

export const currentLocale = (): Locale => (isLocale(i18next.language) ? i18next.language : 'pl');
